import { Badge, Icon, Tooltip } from "@medentic/polaris-react";
import { ViewIcon, LinkIcon, LockIcon } from "@medentic/polaris-icons";
import useEventMetadata from "@features/events/hooks/useEventMetadata";
import type { IconProps } from "@medentic/polaris-react";

enum BadgeTone {
  SUCCESS = "success",
  ATTENTION = "attention",
  INFO = "info",
  WARNING = "warning",
  CRITICAL = "critical",
}

const DEFAULT_TONE = undefined;

const PAYMENT_SLUG_TO_TONE_MAP: Record<string, BadgeTone | undefined> = {
  platform: BadgeTone.SUCCESS,
  offline: DEFAULT_TONE,
};

// Map slugs to their corresponding tones for event entities
const EVENT_SLUG_TO_TONE_MAP: Record<string, BadgeTone | undefined> = {
  completed: DEFAULT_TONE,
  upcoming: BadgeTone.INFO,
  ongoing: BadgeTone.WARNING,
};

// Map slugs to their corresponding tones for ticket entities
const TICKET_SLUG_TO_TONE_MAP: Record<string, BadgeTone | undefined> = {
  private: DEFAULT_TONE,
  unlisted: DEFAULT_TONE,
  draft: DEFAULT_TONE,
  archived: DEFAULT_TONE,
  expired: BadgeTone.WARNING,
  public: BadgeTone.SUCCESS,
};

const VISIBILITY_SLUG_TO_TONE_MAP: Record<string, BadgeTone | undefined> = {
  PRIVATE: DEFAULT_TONE,
  INLISTED: DEFAULT_TONE,
  DRAFT: BadgeTone.INFO,
  PUBLIC: BadgeTone.SUCCESS,
};

// Map slugs to their corresponding tones for paymentPayout entities
const PAYMENT_PAYOUT_SLUG_TO_TONE_MAP: Record<string, BadgeTone | undefined> = {
  paid: BadgeTone.SUCCESS,
  pending: BadgeTone.INFO,
};

const AGENDA_SLUG_TO_TONE_MAP: Record<string, BadgeTone | undefined> = {
  Break: BadgeTone.WARNING,
  Practice: BadgeTone.INFO,
  Lecture: BadgeTone.INFO,
};

// General icon mapping system
const ENTITY_ICON_MAP: Record<
  string,
  Record<string | number, IconProps["source"]>
> = {
  visibility: {
    // By slug
    'PUBLIC': ViewIcon,
    'UNLISTED': LinkIcon,
    'PRIVATE': LockIcon,
    'DRAFT': LockIcon,
    // By id
    1: ViewIcon,    // PUBLIC
    10: LinkIcon,   // UNLISTED
    0: LockIcon,    // PRIVATE
    11: LockIcon,   // DRAFT
  },
  // Add other entities here in the future
  // event: { ... },
  // ticket: { ... },
};

// Helper function to get visibility icon by id or slug
const getVisibilityIcon = (visibility: string | number | undefined) => {
  if (visibility === undefined) return null;
  return ENTITY_ICON_MAP.visibility[visibility] || null;
};

// Component that renders visibility icon with tooltip
interface VisibilityIconProps {
  visibility: string | number | undefined;
  tone?: IconProps["tone"];
}

export function VisibilityIcon({ visibility, tone = "subdued" }: VisibilityIconProps) {
  const { metadata } = useEventMetadata();
  
  const iconSource = getVisibilityIcon(visibility);
  if (!iconSource) return null;
  
  // Find visibility status by slug or id to get label/description
  const visibilityStatus = metadata?.visibility?.find(
    (status) => status.slug === visibility || status.id === visibility
  );
  
  const tooltipContent = visibilityStatus?.label || visibilityStatus?.description;
  
  const iconElement = <Icon source={iconSource} tone={tone} />;
  
  if (tooltipContent) {
    return (
      <Tooltip content={tooltipContent}>
        {iconElement}
      </Tooltip>
    );
  }
  
  return iconElement;
}

interface EventBadgeRendererProps {
  slug: string|number;
  entity:
    | "payment"
    | "event"
    | "ticket"
    | "paymentPayout"
    | "agenda"
    | "visibility";
}

export default function EventBadgeRenderer({
  slug,
  entity,
}: EventBadgeRendererProps) {
  const { metadata } = useEventMetadata();

  // Find the payment processed platform item by slug (no id field in this data)
  const paymentPlatform = metadata?.paymentProcessedPlatform?.find(
    (platform) => platform.slug === slug
  );

  // Find event status by slug (no id field in this data)
  const eventStatus = metadata?.eventStatus?.find(
    (status) => status.slug === slug
  );

  // Find ticket status by slug (no id field in this data)
  const ticketStatus = metadata?.ticketStatus?.find(
    (status) => status.slug === slug
  );

  // Find visibility status by slug or id (has both fields)
  const visibilityStatus = metadata?.visibility?.find(
    (status) => status.slug === slug || status.id === slug
  );

  // Find payment payout by slug (no id field in this data)
  const paymentPayout = metadata?.paymentPayout?.find(
    (status) => status.slug === slug
  );

  // Function to get the appropriate icon based on entity and slug/id
  const getIconForEntity = (entity: string, slug: string | number) => {
    const entityIconMap = ENTITY_ICON_MAP[entity];
    if (!entityIconMap) {
      return null; // No icon mapping for this entity
    }
    
    // Try to find icon by slug/id
    return entityIconMap[slug] || null;
  };

  // Handle both tone, statusText, and description based on entity type
  let tone: BadgeTone | undefined;
  let statusText: string;
  let description: string | undefined;
  const iconSource = getIconForEntity(entity, slug);

  switch (entity) {
    case "payment":
      tone = PAYMENT_SLUG_TO_TONE_MAP[slug] ?? DEFAULT_TONE;
      statusText = paymentPlatform?.label || slug.toString();
      description = paymentPlatform?.description;
      break;
    case "event":
      tone = EVENT_SLUG_TO_TONE_MAP[slug] ?? DEFAULT_TONE;
      statusText = eventStatus?.label || slug.toString();
      description = eventStatus?.description;
      break;
    case "ticket":
      tone = TICKET_SLUG_TO_TONE_MAP[slug] ?? DEFAULT_TONE;
      statusText = ticketStatus?.label || slug.toString();
      description = ticketStatus?.description;
      break;
    case "visibility": {
      // For visibility, we need to check the actual slug from the found item
      const visibilitySlug = visibilityStatus?.slug;
      tone = visibilitySlug
        ? VISIBILITY_SLUG_TO_TONE_MAP[visibilitySlug] ?? DEFAULT_TONE
        : DEFAULT_TONE;
      statusText = visibilityStatus?.label || slug.toString();
      description = visibilityStatus?.description;
      break;
    }
    case "paymentPayout":
      tone = PAYMENT_PAYOUT_SLUG_TO_TONE_MAP[slug] ?? DEFAULT_TONE;
      statusText = paymentPayout?.label || slug.toString();
      description = paymentPayout?.description;
      break;
    case "agenda":
      tone = AGENDA_SLUG_TO_TONE_MAP[slug] ?? DEFAULT_TONE;
      statusText = slug.toString();
      description = undefined; // Agenda items don't have descriptions in metadata
      break;
    default:
      tone = PAYMENT_SLUG_TO_TONE_MAP[slug] ?? DEFAULT_TONE;
      statusText = slug.toString();
      description = undefined;
      break;
  }

  const badgeContent = (
    <span style={{ whiteSpace: 'nowrap' }}>
      {iconSource ? (
        <Badge tone={tone} icon={iconSource}>{statusText}</Badge>
      ) : (
        <Badge tone={tone}>{statusText}</Badge>
      )}
    </span>
  );

  // Wrap with tooltip if description is available
  if (description) {
    return (
      <Tooltip content={description}>
        {badgeContent}
      </Tooltip>
    );
  }

  return badgeContent;
}
